/**
 * @file Public landing page — pages/Landing/WhoWeArePage.tsx.
 */

import React from "react";
import { Link } from "react-router-dom";
import "./LandingPage.css";
import "./ContentPage.css";

const WhoWeArePage: React.FC = () => {
  return (
    <div className="landing-root">
      {/* Top Header */}
      <header className="landing-header flex justify-center items-center space-x-3 text-center py-4">
        <h1 className="landing-main-title text-4xl font-bold text-primary">
          ICoMEC
        </h1>
        <h2 className="landing-sub-title text-xl text-grayDark">
          Indian Construction Materials Embodied Carbon Data Base
        </h2>
      </header>

      {/* Navigation Bar */}
      <nav className="landing-nav">
        <Link to="/">Home</Link>
        <Link to="/join">Join</Link>
        <Link to="/impact">Impact</Link>
        <Link to="/research">Research and Analysis</Link>
        <Link to="/about">About</Link>
      </nav>

      {/* Content Section */}
      <section className="content-page">
        <h1 className="content-title">Who We Are</h1>
        
        <div className="content-section">
          <h2>A Multidisciplinary Collective</h2>
          <p>
            The strength of ICoMEC lies in the diversity of our perspectives. We are a unified group of specialists dedicated to advancing building sector decarbonization through a unique blend of technical expertise and practical application.
          </p>
          <p>Our team brings together:</p>
          <ul className="content-list">
            <li><strong>Life Cycle Assessment (LCA) Practitioners</strong> who ensure every calculation meets international environmental standards.</li>
            <li><strong>Built Environment Researchers</strong> who understand the complexities of modern architecture and construction.</li>
            <li><strong>Data Analysts</strong> who transform vast amounts of industrial information into clear, actionable insights.</li>
            <li><strong>Sustainability Professionals</strong> who advocate for a resilient and low-carbon future.</li>
          </ul>
        </div>

        <div className="content-section">
          <h2>Decades of Combined Experience</h2>
          <p>
            We operate where theoretical science meets real-world implementation. Our collective background spans the entire lifecycle of construction, ensuring that the ICoMEC platform is grounded in reality.
          </p>
          <p>Our core competencies include:</p>
          <ul className="content-list">
            <li><strong>Advanced Materials:</strong> Deep knowledge of building systems and their environmental footprints.</li>
            <li><strong>Climate-Responsive Design:</strong> Integrating carbon reduction strategies with functional, site-specific design.</li>
            <li><strong>Carbon Assessment:</strong> Specializing in high-fidelity embodied carbon modeling for complex projects.</li>
            <li><strong>Policy & Standards:</strong> Contributing to the development of benchmarks that shape industry regulations.</li>
          </ul>
        </div>

        <div className="content-section">
          <h2>At the Intersection of Science and Action</h2>
          <p>
            We believe that credible climate action begins with high-quality data and, more importantly, honest conversations about its complexities.
          </p>
          <p>
            By situating our work at the intersection of science, practice, and decision-making, we ensure that ICoMEC is a foundation for embodied carbon integrity in the construction industry. We are committed to providing the clarity needed to make progress possible.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-left">
            <h2>ICoMEC</h2>
            <p>
              Indian Construction Materials Embodied
              <br /> Carbon Data Base
            </p>
          </div>

          <div className="footer-links">
            <div>
              <h4>About Us</h4>
              <ul>
                <li><Link to="/who-we-are">Who We Are</Link></li>
                <li><Link to="/what-we-do">What We Do</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/partners">Partners</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/glossary">Glossary</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                {/* <li><Link to="/resource-library">Resource Library</Link></li> */}
                <li><Link to="/events">Events</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <p>
          <Link to="/cookies-policy">Cookies Policy</Link> · <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/legal">Legal</Link> · <Link to="/terms-of-use">Terms of Use</Link>
          {/* · <Link to="/imprint">Imprint</Link> */}
        </p>
        <p>© 2026 CARBSE. All rights reserved.</p>
      </div>
    </div>
  );
};

/** React component — Who We Are Page. */
export default WhoWeArePage;
